import { useState, useMemo } from 'react';
import AnalyticsCard from './AnalyticsCard';

const COLUMNS = [
  { key: 'claimId',   label: 'Claim ID'  },
  { key: 'createdAt', label: 'Date'      },
  { key: 'user',      label: 'User'      },
  { key: 'make',      label: 'Make'      },
  { key: 'model',     label: 'Model'     },
  { key: 'status',    label: 'Status'    },
  { key: 'uploaded',  label: 'Uploaded'  },
  { key: 'relevant',  label: 'Relevant'  },
  { key: 'partsId',   label: 'Parts ID'  },
  { key: 'cameras',   label: 'Cameras'   },
];

const PAGE = 20;

export default function DataTable({ data }) {
  const [sortCol, setSortCol] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage]       = useState(0);

  const sorted = useMemo(() => {
    return [...data].sort((a, b) => {
      const va = a[sortCol] ?? '';
      const vb = b[sortCol] ?? '';
      const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortCol, sortDir]);

  const pages    = Math.ceil(sorted.length / PAGE);
  const pageData = sorted.slice(page * PAGE, (page + 1) * PAGE);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir('asc'); }
    setPage(0);
  };

  const btnCls = 'text-xs px-2.5 py-1 rounded-lg border border-border-primary disabled:opacity-40 hover:bg-background-hover transition-colors';

  return (
    <AnalyticsCard title={`Claims Data Table (${data.length.toLocaleString()} rows)`}>
      <div className="overflow-x-auto rounded-lg border border-border-primary">
        <table className="w-full text-xs">
          <thead className="bg-background-primary">
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  className="text-left px-3 py-2.5 text-text-secondary font-medium cursor-pointer select-none hover:text-text-primary whitespace-nowrap"
                >
                  <span className="flex items-center gap-1">
                    {col.label}
                    {sortCol === col.key && (
                      <span className="text-primary">{sortDir === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageData.map((row, i) => (
              <tr key={row.claimId + i} className="border-t border-border-primary hover:bg-background-hover transition-colors">
                <td className="px-3 py-2 font-mono text-[10px] text-text-secondary whitespace-nowrap">{row.claimId}</td>
                <td className="px-3 py-2 text-text-secondary whitespace-nowrap">{row.createdAt}</td>
                <td className="px-3 py-2 text-text-primary max-w-[140px] truncate">{row.user}</td>
                <td className="px-3 py-2 text-text-secondary">{row.make || '—'}</td>
                <td className="px-3 py-2 text-text-secondary">{row.model || '—'}</td>
                <td className="px-3 py-2">
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
                    style={
                      row.status === 'Completed'
                        ? { background: '#10b98118', color: '#10b981' }
                        : { background: '#ef444418', color: '#ef4444' }
                    }
                  >
                    {row.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right text-text-secondary">{row.uploaded}</td>
                <td className="px-3 py-2 text-right text-text-secondary">{row.relevant}</td>
                <td className="px-3 py-2 text-right text-text-secondary">{row.partsId}</td>
                <td className="px-3 py-2 text-right text-text-secondary">{row.cameras}/8</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-text-secondary">
            Showing {page * PAGE + 1}–{Math.min((page + 1) * PAGE, sorted.length)} of {sorted.length.toLocaleString()}
          </p>
          <div className="flex gap-1">
            <button onClick={() => setPage(0)}             disabled={page === 0}          className={btnCls}>«</button>
            <button onClick={() => setPage((p) => p - 1)} disabled={page === 0}          className={btnCls}>‹</button>
            {Array.from({ length: Math.min(5, pages) }, (_, i) => {
              const p = Math.max(0, Math.min(page - 2, pages - 5)) + i;
              return (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`text-xs px-2.5 py-1 rounded-lg border ${
                    p === page
                      ? 'bg-primary text-white border-primary'
                      : 'border-border-primary hover:bg-background-hover'
                  }`}
                >
                  {p + 1}
                </button>
              );
            })}
            <button onClick={() => setPage((p) => p + 1)} disabled={page >= pages - 1} className={btnCls}>›</button>
            <button onClick={() => setPage(pages - 1)}    disabled={page >= pages - 1} className={btnCls}>»</button>
          </div>
        </div>
      )}
    </AnalyticsCard>
  );
}
